import { useState } from 'react';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { FAQ } from '../components/FAQ';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { PriceEstimator } from '../components/PriceEstimator';
import { Link } from 'react-router-dom';
import { 
  Sparkles, Eye, Layers, Timer,
  CheckCircle, Phone, ArrowRight, Calculator
} from 'lucide-react';
import { ContactDropdown } from '../components/ContactDropdown';

export const PaintCorrectionPage = () => {
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  const benefits = [
    { icon: <Sparkles className="w-6 h-6" />, title: 'Remove Swirl Marks', description: 'Eliminate those spider-web scratches that dull your paint\'s appearance.' },
    { icon: <Eye className="w-6 h-6" />, title: 'Restore Clarity', description: 'Bring back the deep, mirror-like finish your car had when new.' },
    { icon: <Layers className="w-6 h-6" />, title: 'Prepare for Coating', description: 'Essential step before ceramic coating for maximum adhesion and longevity.' },
    { icon: <Timer className="w-6 h-6" />, title: 'Long-Lasting Results', description: 'Properly corrected paint maintains its shine for years with proper care.' },
  ];

  const correctionLevels = [
    {
      level: 'Stage 1',
      title: 'Enhancement Polish',
      description: 'Light polish to remove minor swirls and enhance gloss. Ideal for newer vehicles or well-maintained paint.',
      removes: ['Light swirls', 'Water spots', 'Minor marring']
    },
    {
      level: 'Stage 2',
      title: 'Paint Correction',
      description: 'Two-stage process to remove moderate defects. Cutting compound followed by finishing polish.',
      removes: ['Medium scratches', 'Swirl marks', 'Buffer trails', 'Oxidation']
    },
    {
      level: 'Stage 3',
      title: 'Full Correction',
      description: 'Multi-stage correction for severely neglected paint. Restores showroom finish to damaged clear coat.',
      removes: ['Deep scratches', 'Heavy swirls', 'Severe oxidation', 'RIDS (random isolated deep scratches)']
    }
  ];

  const faqItems = [
    {
      question: 'What is paint correction and do I need it?',
      answer: 'Paint correction is the process of removing imperfections from your car\'s clear coat using machine polishing techniques. If you see swirl marks, scratches, or your paint looks dull under direct light, you likely need paint correction. It\'s especially important before applying ceramic coating for optimal results.'
    },
    {
      question: 'How much does paint correction cost in Fresno?',
      answer: 'Paint correction in Fresno typically ranges from $200-$800+ depending on vehicle size and severity of defects. Stage 1 enhancement starts around $200, while full multi-stage correction on larger vehicles can exceed $800. Contact us at 559-880-1818 for an accurate quote.'
    },
    {
      question: 'Will paint correction remove all scratches?',
      answer: 'Paint correction can remove scratches that haven\'t penetrated through the clear coat. We can typically remove 70-95% of defects depending on their depth. Scratches that have gone through to the base coat or primer may require touch-up paint or repainting.'
    },
    {
      question: 'How long does paint correction last?',
      answer: 'The results of paint correction are permanent - we\'re actually removing material from the clear coat. However, new swirls and scratches can develop over time from improper washing. We recommend pairing correction with ceramic coating for long-term protection.'
    },
    {
      question: 'Do you offer paint correction in Madera and Clovis?',
      answer: 'Yes! We provide mobile paint correction services throughout Fresno, Madera, Clovis, Chowchilla, and surrounding Central California areas. We bring professional-grade equipment directly to your location.'
    },
    {
      question: 'How long does paint correction take?',
      answer: 'Depending on the correction level needed, the process takes 4-12+ hours. A single-stage enhancement polish may take 4-6 hours, while a full multi-stage correction can take 10-12+ hours. We never rush the process to ensure optimal results.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Paint Correction Service Fresno",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Noble Detailing",
      "telephone": "559-880-1818",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Fresno",
        "addressRegion": "CA"
      }
    },
    "areaServed": ["Fresno", "Madera", "Clovis", "Chowchilla"],
    "description": "Professional paint correction services in Fresno, CA. Remove swirl marks, scratches, and oxidation. Restore your car's showroom shine."
  };

  return (
    <>
      <SEOHead
        title="Paint Correction Fresno | Swirl & Scratch Removal | Noble Detailing"
        description="Expert paint correction in Fresno, Madera & Clovis. Remove swirls, scratches, and oxidation. Restore your car's showroom shine. Call 559-880-1818 for free quote."
        keywords="paint correction fresno, swirl removal fresno, scratch removal fresno, paint polishing madera, car buffing clovis, paint restoration fresno, auto detailing paint correction, professional polishing central california"
        canonical="https://nobledetailing.com/paint-correction-fresno"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center pt-20" data-testid="correction-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1678383407763-4e02ba2aa056?w=1920&q=80"
            alt="Professional paint correction polishing in Fresno"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600">/</span>
              <span className="text-cyan-400">Paint Correction</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight uppercase tracking-wide">
              Paint Correction<br />
              <span className="text-cyan-400">Fresno & Central CA</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Restore your vehicle's showroom shine with professional paint correction. Our certified detailers remove <strong>swirl marks, scratches, and oxidation</strong> to reveal flawless, mirror-like paint. Serving <strong>Fresno, Madera, Clovis, Chowchilla</strong> and surrounding areas.
            </p>

            <div className="flex flex-wrap gap-4">
              <ContactDropdown 
                label="Get Free Quote" 
                className="btn-primary"
                testId="correction-cta"
              />
              <button 
                onClick={() => setIsEstimatorOpen(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" /> Price Estimate
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Price Estimator Modal */}
      <PriceEstimator 
        isOpen={isEstimatorOpen} 
        onClose={() => setIsEstimatorOpen(false)} 
        defaultService="correction"
      />

      {/* Benefits Section */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="correction-benefits">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Why Paint Correction?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Machine polishing is the only way to truly remove swirls and scratches from your clear coat, not just fill them like wax.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-neutral-900/50 border border-white/5 hover:border-cyan-500/30 transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mx-auto mb-6">
                  {benefit.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="section-padding" data-testid="correction-gallery">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Paint Correction Results
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See the dramatic difference professional paint correction makes on vehicles in Fresno and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                Paint Correction & Ceramic Coating
              </p>
              <BeforeAfterSlider
                beforeImage="/gallery/paintcorrection/before.jpg"
                afterImage="/gallery/paintcorrection/after.jpg"
                className="aspect-[4/3]"
              />
            </div>
            <div>
              <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                Exterior Detail & Sealant
              </p>
              <BeforeAfterSlider
                beforeImage="/gallery/paintcorrection/before1.jpg"
                afterImage="/gallery/paintcorrection/after1.jpg"
                className="aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Correction Levels */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="correction-levels">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Correction Levels
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We assess your paint's condition and recommend the appropriate level of correction for optimal results.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {correctionLevels.map((level, index) => (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-neutral-900 border border-white/10 p-8 hover:border-cyan-500/30 transition-all"
              >
                <div className="font-accent text-cyan-400 text-sm uppercase tracking-wider mb-2">
                  {level.level}
                </div>
                <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-4">
                  {level.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {level.description}
                </p>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-3">Removes:</p>
                  <ul className="space-y-2">
                    {level.removes.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-cyan-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding" data-testid="correction-areas">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
                Paint Correction Service Areas
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Noble Detailing provides professional paint correction services throughout Central California. Our mobile service brings professional-grade equipment to your location.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Fresno', 'Madera', 'Clovis', 'Chowchilla', 'Sanger', 'Selma', 'Visalia', 'Hanford'].map((city) => (
                  <div key={city} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-500" />
                    <span>{city}, CA</span>
                  </div>
                ))}
              </div>

              <ContactDropdown 
                label="Contact Us" 
                className="btn-primary"
                testId="correction-service-area-contact"
              />
            </div>

            <div className="bg-neutral-900 border border-white/10 p-8">
              <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-6">
                Our Process
              </h3>
              <ul className="space-y-4">
                {[
                  'Free paint inspection & assessment',
                  'Thorough wash & decontamination',
                  'Clay bar surface treatment',
                  'Multi-stage machine polishing',
                  'IPA wipe-down & inspection',
                  'Final gloss enhancement',
                  'Sealant or coating application',
                  'Detailed care instructions'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="bg-[#0a0a0a]">
        <FAQ items={faqItems} title="Paint Correction FAQ - Fresno" />
      </div>

      {/* CTA Section */}
      <section className="section-padding" data-testid="correction-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
            Ready to Restore Your Paint?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Get a free paint inspection and quote for professional correction services in Fresno, Madera, Clovis, and surrounding areas.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ContactDropdown 
              label="Contact Us" 
              className="btn-primary"
              testId="correction-bottom-contact"
            />
            <Link 
              to="/ceramic-coating-fresno"
              className="btn-secondary"
            >
              Add Ceramic Coating
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
